/**
 * Cloud evaluation fallback using Lichess Cloud Eval API
 * Provides reliable analysis when local Stockfish fails or times out
 */

export interface CloudEvalResult {
  fen: string;
  depth: number;
  evaluation: number;
  bestMove: string | null;
  pv: string[];
  source: 'lichess-cloud';
}

/**
 * Get position evaluation from Lichess Cloud Eval
 */
export async function cloudEvaluate(
  fen: string, 
  multiPV = 1, 
  depth = 20
): Promise<CloudEvalResult | null> {
  try {
    const url = new URL('https://lichess.org/api/cloud-eval');
    url.searchParams.set('fen', fen);
    url.searchParams.set('multiPv', multiPV.toString());
    if (depth) url.searchParams.set('variant', 'standard');

    const response = await fetch(url.toString(), {
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.warn(`[Cloud Eval] API error: ${response.status}`);
      return null;
    }

    const data = await response.json();
    
    if (!data.pvs || !Array.isArray(data.pvs) || data.pvs.length === 0) {
      console.warn('[Cloud Eval] No analysis data in response');
      return null;
    }

    const mainLine = data.pvs[0];
    if (!mainLine) return null;

    // Convert Lichess centipawn evaluation to our format
    let evaluation = 0;
    if (mainLine.cp !== undefined) {
      evaluation = mainLine.cp;
    } else if (mainLine.mate !== undefined) {
      // Convert mate scores: positive = white wins, negative = black wins
      evaluation = mainLine.mate > 0 ? 29900 + mainLine.mate : -29900 - mainLine.mate;
    }

    // Extract best move (first move in PV)
    const pv = mainLine.moves ? mainLine.moves.split(' ') : [];
    const bestMove = pv.length > 0 ? pv[0] : null;

    return {
      fen,
      depth: mainLine.depth || depth,
      evaluation,
      bestMove,
      pv,
      source: 'lichess-cloud'
    };

  } catch (error) {
    console.warn('[Cloud Eval] Request failed:', error);
    return null;
  }
}

/**
 * Check if cloud evaluation is available (simple connectivity test)
 */
export async function isCloudEvalAvailable(): Promise<boolean> {
  try {
    const testFen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    const result = await cloudEvaluate(testFen, 1, 1);
    return result !== null;
  } catch {
    return false;
  }
}